/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useRef, useState } from 'react'
import { Button, Form, Input, Modal, Pagination, Select, Space, Table } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import CheckIcon from '@mui/icons-material/Check'

import axios from '../apiClient'
import Alert from '../alert'

function getDebtRows(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.debt)) return data.debt
  if (Array.isArray(data?.members)) return data.members
  if (Array.isArray(data?.debtMembers)) return data.debtMembers
  if (Array.isArray(data?.debts)) return data.debts
  if (Array.isArray(data?.list)) return data.list
  if (Array.isArray(data?.rows)) return data.rows
  if (Array.isArray(data?.data)) return data.data
  return []
}

function getTotalCount(data, fallbackLength) {
  return (
    data?.pagination?.total ??
    data?.total ??
    data?.count ??
    fallbackLength
  )
}

function getMemberId(row) {
  return row?.memberId || row?.member_id || row?.id || row?.member?.id
}

function getMemberName(row) {
  return row?.username || row?.name || row?.member?.username || row?.member?.name || '-'
}

function getPhone(row) {
  return row?.phone_number || row?.phoneNumber || row?.member?.phone_number || row?.member?.phoneNumber || '-'
}

function getDebtAmount(row) {
  return Number(
    row?.totalAmount ??
    row?.debtAmount ??
    row?.debt_amount ??
    row?.debt ??
    row?.amount ??
    row?.outstanding ??
    0
  ) || 0
}

export default function MemberDebtList() {
  const [debts, setDebts] = useState([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [errorMsg, setErrorMsg] = useState({})
  const [openAlert, setOpenAlert] = useState(false)
  const [form] = Form.useForm()
  const selectedMember = useRef(null)

  async function fetchData() {
    setLoading(true)
    const res = await axios.get(`/wallet/getDebtMembers?current=${page}&pageSize=10`)

    if (res.code != 200) {
      if (res.code == 404) {
        setDebts([])
        setTotal(0)
        setLoading(false)
        return
      }
      setErrorMsg({ code: res.code, msg: res.message })
      setOpenAlert(true)
      setLoading(false)
      return
    }

    const rows = getDebtRows(res.data)
    setDebts(rows)
    setTotal(getTotalCount(res.data, rows.length))
    setLoading(false)
  }

  async function getPaymentMethod() {
    const res = await axios.get(`/member/getPaymentChannelList`)
    if (res.code == 200) {
      const arr = (res?.data || []).map((v) => ({
        value: v?.channel_name,
        label: v?.display_name
      }))
      setPaymentMethods(arr)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page])

  useEffect(() => {
    getPaymentMethod()
  }, [])

  const handleCloseAlert = () => setOpenAlert(false)
  const handlePageChange = (page) => setPage(page)

  const handleReceive = async (close) => {
    try {
      await form.validateFields()
    } catch (error) {
      return
    }

    const row = selectedMember.current
    const memberId = getMemberId(row)
    if (!memberId) {
      setErrorMsg({ code: 500, msg: 'memberId not found' })
      setOpenAlert(true)
      return
    }

    setLoading(true)
    close()
    const { amount, paymentMethod } = form.getFieldsValue()
    const params = {
      memberId,
      amount,
      paymentMethod
    }
    const res = await axios.post(`/wallet/setDebtByMemberId`, params)
    if (res.code == 200) {
      form.resetFields()
    }
    setErrorMsg({ code: res.code, msg: res.message })
    setOpenAlert(true)
    fetchData()
  }

  const { confirm } = Modal
  const showReceiveModal = (row) => {
    selectedMember.current = row
    form.setFieldsValue({
      amount: getDebtAmount(row),
      paymentMethod: undefined
    })

    confirm({
      mask: false,
      centered: true,
      maskClosable: true,
      okText: 'ချေမယ်',
      title: `${getMemberName(row)} အကြွေးချေမယ်`,
      icon: <ExclamationCircleFilled />,
      onOk(close) {
        handleReceive(close)
      },
      content:
        <Form
          name="member-debt-form"
          form={form}
          autoComplete="off"
          style={{ marginTop: '1rem' }}
        >
          <Form.Item
            label="Amount"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            name="amount"
            rules={[
              {
                required: true,
                message: 'Please input amount!'
              }
            ]}
            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
            css={css`
              .ant-form-item-explain-error {
                margin-left: 2rem !important;
                font-size: 0.8rem;
              }
            `}
          >
            <Input type="number" size="large" css={css`margin-left: 2rem;`} />
          </Form.Item>

          <Form.Item
            label="Payment"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            name="paymentMethod"
            rules={[
              {
                required: true,
                message: 'Please select payment method!'
              }
            ]}
            css={css`
              .ant-form-item-explain-error {
                margin-left: 2rem !important;
                font-size: 0.8rem;
              }
            `}
          >
            <Select
              size="large"
              options={paymentMethods}
              dropdownStyle={{ zIndex: 2000 }}
              css={css`margin-left: 2rem;`}
            />
          </Form.Item>
        </Form>
    })
  }

  const columns = [
    {
      title: 'Username',
      key: 'username',
      render: (row) => getMemberName(row)
    },
    {
      title: 'Phone No.',
      key: 'phone',
      render: (row) => getPhone(row)
    },
    {
      title: 'Debt Amount',
      key: 'debtAmount',
      render: (row) => getDebtAmount(row).toLocaleString()
    },
    {
      title: 'Action',
      key: 'action',
      render: (row) => (
        <Button
          type="primary"
          onClick={() => showReceiveModal(row)}
          css={css`
            display: inline-flex;
            align-items: center;
            height: 38px;
            border: none;
            border-radius: 999px;
            padding: 0 16px;
            background: linear-gradient(135deg, #5b8def 0%, #3b82f6 100%);
            box-shadow: 0 8px 18px rgba(59, 130, 246, 0.22);
            :hover {
              background: linear-gradient(135deg, #4f83e8 0%, #2563eb 100%) !important;
              box-shadow: 0 10px 22px rgba(37, 99, 235, 0.28);
            }
          `}
          icon={<CheckIcon css={css`color: white;font-size: 18px;`} />}
        >
          <span css={css`font-weight: 600; letter-spacing: 0.2px;`}>ချေမယ်</span>
        </Button>
      )
    }
  ]

  return (
    <div>
      <Alert openAlert={openAlert} errorMsg={errorMsg} handleCloseAlert={handleCloseAlert} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Member အကြွေးစာရင်း</h3>
        <Space>
          <span>Total: {Number(total || 0).toLocaleString()}</span>
        </Space>
      </div>
      <Table
        rowKey={(row) => getMemberId(row) || `${getMemberName(row)}-${getPhone(row)}`}
        loading={loading}
        scroll={{ x: 'max-content' }}
        css={css`padding-top: 1.5rem;`}
        pagination={false}
        columns={columns}
        dataSource={debts}
      />
      <Pagination
        className='pagination'
        css={css`display: flex;justify-content: end;padding-top: 1rem;`}
        current={page}
        total={total}
        pageSize={10}
        showSizeChanger={false}
        onChange={handlePageChange}
      />
    </div>
  )
}
